import React, { useEffect, useState } from 'react';
import {
  adminGetInstruments, adminCreateInstrument, adminUpdateInstrument, adminDeleteInstrument,
  adminGetGenres,      adminCreateGenre,      adminUpdateGenre,      adminDeleteGenre,
} from '../services/api';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';

function InstrumentsTab() {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [msg, setMsg]                 = useState('');

  // Create form
  const [newName, setNewName]               = useState('');
  const [newAllowSpecify, setNewAllowSpecify] = useState(false);
  const [creating, setCreating]             = useState(false);

  // Edit state: { [id]: { name, allow_specify, saving } }
  const [edits, setEdits] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deleting, setDeleting]           = useState(false);

  const load = () => {
    adminGetInstruments()
      .then(res => setInstruments(res.data))
      .catch(() => setError('Failed to load instruments.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (inst) => {
    setEdits(e => ({
      ...e,
      [inst.instrument_type_id]: { name: inst.name, allow_specify: inst.allow_specify, saving: false }
    }));
  };

  const cancelEdit = (id) => {
    setEdits(e => { const n = { ...e }; delete n[id]; return n; });
  };

  const handleUpdate = async (id) => {
    const edit = edits[id];
    if (!edit) return;
    setEdits(e => ({ ...e, [id]: { ...e[id], saving: true } }));
    try {
      await adminUpdateInstrument(id, { name: edit.name, allow_specify: edit.allow_specify });
      setMsg('Saved.');
      cancelEdit(id);
      load();
    } catch {
      setMsg('Save failed.');
      setEdits(e => ({ ...e, [id]: { ...e[id], saving: false } }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true); setMsg('');
    try {
      await adminCreateInstrument({ name: newName.trim(), allow_specify: newAllowSpecify });
      setMsg('Created.');
      setNewName(''); setNewAllowSpecify(false);
      load();
    } catch {
      setMsg('Create failed.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminDeleteInstrument(deleteConfirm.id);
      setInstruments(prev => prev.filter(i => i.instrument_type_id !== deleteConfirm.id));
      setDeleteConfirm(null);
      setMsg('Removed.');
    } catch (err) {
      console.error('Delete instrument failed:', err?.response?.data || err?.message);
      setMsg('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--muted)', padding: '1rem' }}>Loading…</div>;
  if (error)   return <div className="a-error">{error}</div>;

  return (
    <div>
      {msg && <div style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: msg.includes('failed') ? 'var(--red)' : 'var(--green)' }}>{msg}</div>}

      <div className="a-card" style={{ marginBottom: '1.25rem', padding: '1rem' }}>
        <div className="a-section-title">Add Instrument Type</div>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 180px' }}>
            <label className="a-label">Name</label>
            <input className="a-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Bass Guitar" required />
          </div>
          <div style={{ flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '1px' }}>
            <input
              type="checkbox"
              id="new-allow-specify"
              checked={newAllowSpecify}
              onChange={e => setNewAllowSpecify(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="new-allow-specify" style={{ fontSize: '0.875rem', color: 'var(--text)', cursor: 'pointer' }}>
              Allow "Specify" field
            </label>
          </div>
          <button type="submit" className="a-btn a-btn--navy a-btn--sm" disabled={creating} style={{ marginBottom: '1px' }}>
            {creating ? 'Adding…' : 'Add'}
          </button>
        </form>
      </div>

      {deleteConfirm && (
        <div className="a-modal-backdrop">
          <div className="a-modal">
            <div className="a-modal__title">Delete Instrument Type</div>
            <p style={{ marginBottom: '1.25rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              Are you sure you want to remove <strong>{deleteConfirm.name}</strong>? It will no longer appear as an option for performers.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</button>
              <button className="a-btn a-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="a-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Allow Specify</th>
              <th style={{ width: '190px' }}></th>
            </tr>
          </thead>
          <tbody>
            {instruments.map(inst => {
              const editing = edits[inst.instrument_type_id];
              return (
                <tr key={inst.instrument_type_id}>
                  <td>
                    {editing ? (
                      <input
                        className="a-input"
                        value={editing.name}
                        onChange={e => setEdits(ed => ({ ...ed, [inst.instrument_type_id]: { ...ed[inst.instrument_type_id], name: e.target.value } }))}
                        style={{ margin: 0 }}
                      />
                    ) : inst.name}
                  </td>
                  <td>
                    {editing ? (
                      <input
                        type="checkbox"
                        checked={editing.allow_specify}
                        onChange={e => setEdits(ed => ({ ...ed, [inst.instrument_type_id]: { ...ed[inst.instrument_type_id], allow_specify: e.target.checked } }))}
                        style={{ width: '16px', height: '16px' }}
                      />
                    ) : (inst.allow_specify ? 'Yes' : 'No')}
                  </td>
                  <td>
                    {editing ? (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="a-btn a-btn--navy a-btn--sm" onClick={() => handleUpdate(inst.instrument_type_id)} disabled={editing.saving}>
                          {editing.saving ? '…' : 'Save'}
                        </button>
                        <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => cancelEdit(inst.instrument_type_id)}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => startEdit(inst)}>Edit</button>
                        <button className="a-btn a-btn--danger a-btn--sm" onClick={() => setDeleteConfirm({ id: inst.instrument_type_id, name: inst.name })}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {instruments.length === 0 && (
              <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--muted)' }}>No instrument types yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GenresTab() {
  const [genres, setGenres]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [msg, setMsg]           = useState('');
  const [newName, setNewName]             = useState('');
  const [creating, setCreating]           = useState(false);
  const [edits, setEdits]                 = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  const load = () => {
    adminGetGenres()
      .then(res => setGenres(res.data))
      .catch(() => setError('Failed to load genres.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const startEdit  = (g) => setEdits(e => ({ ...e, [g.genre_id]: { name: g.name, saving: false } }));
  const cancelEdit = (id) => setEdits(e => { const n = { ...e }; delete n[id]; return n; });

  const handleUpdate = async (id) => {
    const edit = edits[id];
    if (!edit) return;
    setEdits(e => ({ ...e, [id]: { ...e[id], saving: true } }));
    try {
      await adminUpdateGenre(id, { name: edit.name });
      setMsg('Saved.');
      cancelEdit(id);
      load();
    } catch {
      setMsg('Save failed.');
      setEdits(e => ({ ...e, [id]: { ...e[id], saving: false } }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true); setMsg('');
    try {
      await adminCreateGenre({ name: newName.trim() });
      setMsg('Created.');
      setNewName('');
      load();
    } catch {
      setMsg('Create failed.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await adminDeleteGenre(deleteConfirm.id);
      setGenres(prev => prev.filter(g => g.genre_id !== deleteConfirm.id));
      setDeleteConfirm(null);
      setMsg('Removed.');
    } catch (err) {
      console.error('Delete genre failed:', err?.response?.data || err?.message);
      setMsg('Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--muted)', padding: '1rem' }}>Loading…</div>;
  if (error)   return <div className="a-error">{error}</div>;

  return (
    <div>
      {msg && <div style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: msg.includes('failed') ? 'var(--red)' : 'var(--green)' }}>{msg}</div>}

      {deleteConfirm && (
        <div className="a-modal-backdrop">
          <div className="a-modal">
            <div className="a-modal__title">Delete Genre</div>
            <p style={{ marginBottom: '1.25rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              Are you sure you want to remove <strong>{deleteConfirm.name}</strong>? It will no longer appear as an option for performers.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</button>
              <button className="a-btn a-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="a-card" style={{ marginBottom: '1.25rem', padding: '1rem' }}>
        <div className="a-section-title">Add Genre</div>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label className="a-label">Name</label>
            <input className="a-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Blues" required />
          </div>
          <button type="submit" className="a-btn a-btn--navy a-btn--sm" disabled={creating} style={{ marginBottom: '1px' }}>
            {creating ? 'Adding…' : 'Add'}
          </button>
        </form>
      </div>

      <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="a-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Genre Name</th>
              <th style={{ width: '190px' }}></th>
            </tr>
          </thead>
          <tbody>
            {genres.map(g => {
              const editing = edits[g.genre_id];
              return (
                <tr key={g.genre_id}>
                  <td>
                    {editing ? (
                      <input
                        className="a-input"
                        value={editing.name}
                        onChange={e => setEdits(ed => ({ ...ed, [g.genre_id]: { ...ed[g.genre_id], name: e.target.value } }))}
                        style={{ margin: 0 }}
                      />
                    ) : g.name}
                  </td>
                  <td>
                    {editing ? (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="a-btn a-btn--navy a-btn--sm" onClick={() => handleUpdate(g.genre_id)} disabled={editing.saving}>
                          {editing.saving ? '…' : 'Save'}
                        </button>
                        <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => cancelEdit(g.genre_id)}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => startEdit(g)}>Edit</button>
                        <button className="a-btn a-btn--danger a-btn--sm" onClick={() => setDeleteConfirm({ id: g.genre_id, name: g.name })}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {genres.length === 0 && (
              <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--muted)' }}>No genres yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState('instruments');

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>

        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Settings</h1>
        </div>

        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: '1.5rem' }}>
          {[
            { id: 'instruments', label: 'Instrument Types' },
            { id: 'genres',      label: 'Genres'           },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.625rem 1.25rem',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--navy)' : '2px solid transparent',
                background: 'none',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 600 : 400,
                color: activeTab === tab.id ? 'var(--navy)' : 'var(--muted)',
                fontSize: '0.9rem',
                marginBottom: '-2px',
                transition: 'color 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'instruments' && <InstrumentsTab />}
        {activeTab === 'genres'      && <GenresTab />}

      </div>
    </>
  );
}
